/**
 * Fred Martin Collection - Shopping Cart Component
 * @version 2.0.0 | Build 20250105.1445
 * @author Fred Martin Studio
 * @description Cart management and item handling
 * Custom shopping cart implementation for Fred Martin Collection
 */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-drawer-items') ||
        this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.progress = this.getAttribute('data-progress');
  }

  connectedCallback() {
    setTimeout(() => {
      this.querySelector('[data-progress-line]').style.width = this.progress;
    }, 100);
  }
}
customElements.define('progress-bar', ProgressBar);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );
    this.debouncedOnChange = debounce(event => {
      this.onChange(event);
    }, 300);
    this.addEventListener(
      'change',
      this.debouncedOnChange.bind(this)
    );

    updateFreeShipping();
  }

  onChange(event) {
    if (event.target.name !== 'updates[]') return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return [
      {
        id: `#shopify-section-${
          document.getElementById('main-cart-items').dataset.id
        }`,
        section:
          document.getElementById('main-cart-items').dataset.id,
        selector: `#shopify-section-${
          document.getElementById('main-cart-items').dataset.id
        } cart-items`
      },
      {
        id: '#cart-counter',
        section: 'fm-bag-count',
        selector: '#shopify-section-fm-bag-count'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    console.log('CartItems.updateQuantity started', { line, quantity, name });
    this.classList.add('is-loading');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(
        section => section.section
      ),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body }
    })
      .then(response => {
        if (!response.ok) {
          // Attempt to get text for logging, then throw
          return response.text().then(text => {
            throw new Error(`Cart update failed: ${response.status} ${response.statusText}. Response body: ${text}`);
          }).catch(() => {
            // If .text() itself fails or if it's not useful
            throw new Error(`Cart update failed: ${response.status} ${response.statusText}. Could not read response body.`);
          });
        }
        return response.text();
      })
      .then(state => {
        const parsedState = JSON.parse(state);
        console.log('Response from routes.cart_change_url (parsed state):', parsedState);

        this.getSectionsToRender().forEach(section => {
          try {
            const elementToReplace =
              section.id === '#cart-counter'
                ? document.querySelector(section.id)
                : document.querySelector(section.selector);

            if (elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            } else {
              console.error("CartItems.updateQuantity: Could not find elementToReplace for selector", section.selector);
            }
          } catch (error) {
            console.error("Error updating section HTML for selector:", error, "Section data:", section, "Full parsed state:", parsedState);
          }
        });
        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`))
          lineItem.querySelector(`[name="${name}"]`).focus();

        updateFreeShipping();
      })
      .catch(error => {
        console.error("Error during cart update request (updateQuantity) or parsing:", error, {line, quantity, name});
        // Potentially add a generic error message to an element if one is available for CartItems
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document
        .querySelectorAll(
          `[data-line-item-error][data-line="${line}"]`
        )
        .forEach(error => {
          error.innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            document.querySelector(`[id*="ProductQuantity-${line}"]`)
              .value
          );
        });
    }

    this.currentItemCount = itemCount;
  }
}

customElements.define('cart-items', CartItems);
